import { PREFERRED_ACTIVITY_OPTIONS } from "@/components/assessment/assessmentData";

export const COMPANION_CALENDAR_KEY = "forr:companion-calendar:v1";

export const CALENDAR_EVENT_TYPES = [
  { key: "workout", label: "Workout or training split" },
  { key: "sport", label: "Sport, class, or competition" },
  { key: "recreation", label: "Recreation or skill activity" },
  { key: "recovery", label: "Recovery, mobility, or bedtime routine" },
];

export const WORKOUT_MODALITIES = [
  { key: "strength", label: "Strength / weights" },
  { key: "cardio", label: "Cardio / conditioning" },
  { key: "mixed", label: "Mixed session" },
];

export const USUAL_EFFORT_OPTIONS = [
  { key: "easy", label: "Easy" },
  { key: "moderate", label: "Moderate" },
  { key: "challenging", label: "Challenging" },
  { key: "very-challenging", label: "Very challenging" },
  { key: "variable", label: "Variable" },
];

export const REMINDER_OPTIONS = [
  { value: 0, label: "At start time" },
  { value: 10, label: "10 minutes before" },
  { value: 30, label: "30 minutes before" },
  { value: 60, label: "1 hour before" },
];

const DAY_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

const pad = (value) => String(value).padStart(2, "0");
const localDateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const activityLabel = (key, fallback = "Scheduled activity") =>
  PREFERRED_ACTIVITY_OPTIONS.find((option) => option.key === key)?.label || fallback;

const parseEventDate = (event) => {
  if (!event?.date) return null;
  const [year, month, day] = event.date.split("-").map(Number);
  const [hour, minute] = String(event.time || "12:00").split(":").map(Number);
  const date = new Date(year, month - 1, day, hour, minute, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeEvent = (event = {}) => ({
  id: event.id || `calendar-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  source: event.source || "user",
  title: String(event.title || "Scheduled activity").trim(),
  date: event.date || localDateKey(new Date()),
  time: event.time || "18:00",
  durationMinutes: Math.max(5, Number(event.durationMinutes) || 30),
  category: event.category || "workout",
  modality: event.modality || "mixed",
  usualEffort: event.usualEffort || "moderate",
  repeat: event.repeat || "none",
  weekdays: Array.isArray(event.weekdays) ? event.weekdays : [],
  reminderMinutes: Math.max(0, Number(event.reminderMinutes) || 30),
  description: String(event.description || ""),
  activityKey: event.activityKey || null,
});

export function loadUserCalendarEvents() {
  try {
    const parsed = JSON.parse(localStorage.getItem(COMPANION_CALENDAR_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.map(normalizeEvent) : [];
  } catch (_error) {
    return [];
  }
}

export function saveUserCalendarEvents(events) {
  const normalized = (events || []).map(normalizeEvent);
  try {
    localStorage.setItem(COMPANION_CALENDAR_KEY, JSON.stringify(normalized));
    window.dispatchEvent(new CustomEvent("adaptive-calendar-updated"));
  } catch (_error) {
    // The calendar remains available for the current session.
  }
  return normalized;
}

export function createUserCalendarEvent(values = {}) {
  return normalizeEvent({ ...values, source: "user" });
}

export function assessmentCalendarEvents({ assessment, baselinePlan }) {
  const generated = (baselinePlan?.week?.sessions || [])
    .filter((session) => session.period !== "Scheduled activity")
    .map((session) =>
      normalizeEvent({
        id: `assessment-${assessment?.completedAt || "baseline"}-${session.id}`,
        source: "assessment-plan",
        title: session.activity || session.period,
        date: session.date,
        time: session.time,
        durationMinutes: session.durationMinutes,
        category:
          session.period === "Bedtime support" || session.period === "Morning preparation"
            ? "recovery"
            : "workout",
        modality:
          session.period === "Main physiological input" ? "mixed" : "recovery",
        usualEffort: String(session.intensity || "moderate").toLowerCase().includes("light")
          ? "easy"
          : "moderate",
        reminderMinutes: session.period === "Bedtime support" ? 10 : 30,
        description: [session.preparation, session.recovery].filter(Boolean).join(" "),
      }),
    );

  const recurring = (assessment?.answers?.schedule?.scheduledActivities || []).map((item) =>
    normalizeEvent({
      id: `assessment-recurring-${assessment?.completedAt || "baseline"}-${item.id}`,
      source: "assessment-scheduled",
      title: activityLabel(item.activity),
      date: localDateKey(new Date()),
      time: item.time || "18:00",
      durationMinutes: item.duration || 60,
      category: "sport",
      modality: "mixed",
      usualEffort: item.effort || "moderate",
      repeat: "weekly",
      weekdays: item.days || [],
      reminderMinutes: 30,
      activityKey: item.activity,
      description: "Existing scheduled activity from the Adaptive Assessment.",
    }),
  );

  return [...generated, ...recurring];
}

export function expandCalendarEvents(events, monthDate = new Date()) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const expanded = [];

  (events || []).forEach((event) => {
    if (event.repeat !== "weekly") {
      const date = parseEventDate(event);
      if (date && date.getFullYear() === year && date.getMonth() === month) {
        expanded.push(event);
      }
      return;
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(year, month, day);
      if (!event.weekdays.includes(DAY_CODES[date.getDay()])) continue;
      expanded.push({
        ...event,
        id: `${event.id}-${localDateKey(date)}`,
        recurringId: event.id,
        date: localDateKey(date),
      });
    }
  });

  return expanded.sort((a, b) => {
    const first = parseEventDate(a)?.getTime() || 0;
    const second = parseEventDate(b)?.getTime() || 0;
    return first - second;
  });
}

export function upcomingCalendarEvents(events, now = new Date(), limit = 6) {
  return (events || [])
    .filter((event) => {
      const date = parseEventDate(event);
      return date && date.getTime() >= now.getTime() - 60 * 60 * 1000;
    })
    .sort((a, b) => parseEventDate(a) - parseEventDate(b))
    .slice(0, limit);
}

const intensityRange = ({ plan, checkIn }) => {
  const readiness = checkIn?.physicalReadiness;
  const energy = Number(checkIn?.energy) || 3;
  const target = plan?.currentState?.target;

  if (readiness === "pause") return [0, 0];
  if (readiness === "modify") return [40, 55];
  if (readiness === "light-moderate") return [50, 65];
  if (target === "organize-high-activation") return energy >= 3 ? [75, 85] : [60, 70];
  if (target === "organize-overwhelm") return [65, 75];
  if (["increase-engagement", "restore-activation"].includes(target)) {
    return energy <= 2 ? [55, 65] : [65, 75];
  }
  if (target === "use-available-capacity") return [80, 90];
  if (target === "maintain-capacity") return [70, 80];
  return [65, 75];
};

const approximateHeartRateRange = ({ range, age }) => {
  if (!Number(age) || range[0] <= 0) return null;
  const estimatedMaximum = 220 - Number(age);
  return [
    Math.round((estimatedMaximum * range[0]) / 100),
    Math.round((estimatedMaximum * range[1]) / 100),
  ];
};

export function buildScheduledActivityGuidance({ event, plan, checkIn, identity }) {
  if (!event) return null;
  const state = plan?.currentState?.system || "the current reported state";
  const energy = Number(checkIn?.energy) || 3;
  const readiness = checkIn?.physicalReadiness || "not yet confirmed";

  if (["sport", "recreation"].includes(event.category)) {
    return {
      eventId: event.id,
      preserveActivity: true,
      title: `Keep ${event.title} as planned`,
      intensityPercent: null,
      instruction:
        "Use a gradual activity-specific warm-up, hydrate beforehand, use familiar nourishment when needed, and complete an intentional cooldown afterward.",
      reason: `${state} is the current context. For a sport or recreational commitment, Companion preserves the activity and improves preparation and recovery rather than trying to control every competitive effort.`,
    };
  }

  if (event.category === "recovery") {
    return {
      eventId: event.id,
      preserveActivity: true,
      title: event.title,
      intensityPercent: null,
      instruction: "Keep the routine easy and use it as a transition rather than another training demand.",
      reason: "This event supports preparation or recovery and remains separate from the main state-matched output.",
    };
  }

  const range = intensityRange({ plan, checkIn });
  if (range[0] === 0) {
    return {
      eventId: event.id,
      preserveActivity: false,
      title: `${event.title} should wait or be modified`,
      intensityPercent: "0%",
      instruction:
        "Do not use the normal workout dose while the physical-readiness response says exercise should wait. Use the approved non-exercise option or qualified guidance instead.",
      reason: `The current state remains relevant, but physical readiness is ${readiness} and controls whether the workout can be performed.`,
    };
  }

  const percent = `${range[0]}–${range[1]}%`;
  const heartRate = approximateHeartRateRange({ range, age: identity?.age });
  const measure =
    event.modality === "strength"
      ? `Keep working weight and total effort around ${percent} of your normal session.`
      : event.modality === "cardio"
        ? `Keep effort around ${percent} of your normal session${
            heartRate ? `, approximately ${heartRate[0]}–${heartRate[1]} bpm using a simple age-based estimate` : ""
          }.`
        : `Keep the overall session around ${percent} of your normal working effort.`;

  return {
    eventId: event.id,
    preserveActivity: true,
    title: `${event.title} · ${percent} today`,
    intensityPercent: percent,
    heartRateRange: heartRate,
    instruction: `${measure} Warm up progressively, hydrate before and after, use familiar nourishment when needed, and finish with a gradual cooldown.`,
    reason: `${state} with energy ${energy}/5 and readiness ${readiness} suggests preserving the planned workout while adjusting the dose to improve the chance of useful output followed by homeostatic recovery.`,
  };
}

const escapeIcs = (value) =>
  String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const icsDate = (event, addMinutes = 0) => {
  const date = parseEventDate(event);
  if (!date) return "";
  date.setMinutes(date.getMinutes() + addMinutes);
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(
    date.getHours(),
  )}${pad(date.getMinutes())}00`;
};

export function buildEventIcs(event, guidance) {
  const reminder = Math.max(0, Number(event.reminderMinutes) || 0);
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//FORR Health//APOS Companion//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${escapeIcs(event.id)}@apos-companion`,
    `DTSTAMP:${icsDate({ ...event, date: localDateKey(new Date()), time: new Date().toTimeString().slice(0, 5) })}`,
    `DTSTART:${icsDate(event)}`,
    `DTEND:${icsDate(event, event.durationMinutes)}`,
    `SUMMARY:${escapeIcs(event.title)}`,
    `DESCRIPTION:${escapeIcs([guidance?.instruction, guidance?.reason, event.description].filter(Boolean).join("\n\n"))}`,
    "BEGIN:VALARM",
    `TRIGGER:-PT${reminder}M`,
    "ACTION:DISPLAY",
    `DESCRIPTION:${escapeIcs(`APOS Companion reminder: ${event.title}`)}`,
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

export function downloadEventIcs(event, guidance) {
  const blob = new Blob([buildEventIcs(event, guidance)], { type: "text/calendar;charset=utf-8" });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = `${event.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "apos-event"}.ics`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}

export async function requestCalendarNotifications() {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  return Notification.requestPermission();
}

export function scheduleOpenAppReminders(events) {
  if (!("Notification" in window) || Notification.permission !== "granted") return () => {};
  const now = Date.now();
  const timers = [];

  (events || []).forEach((event) => {
    const start = parseEventDate(event)?.getTime();
    if (!start) return;
    const notifyAt = start - (Number(event.reminderMinutes) || 0) * 60000;
    const delay = notifyAt - now;
    if (delay <= 0 || delay > 24 * 60 * 60 * 1000) return;
    timers.push(
      window.setTimeout(() => {
        new Notification(event.title, {
          body: "Open APOS Companion for your current-state adjustment, preparation, and recovery guidance.",
          icon: "/adaptive-companion-192.png",
          tag: event.id,
        });
      }, delay),
    );
  });

  return () => timers.forEach((timer) => window.clearTimeout(timer));
}
